import {
  CalendarIcon,
  CodeIcon,
  ComponentBooleanIcon,
  FileIcon,
  SpeakerLoudIcon,
  PersonIcon,
  InputIcon,
  TextIcon,
  VideoIcon,
  FrameIcon,
  Component1Icon,
  ComponentInstanceIcon,
  TextAlignJustifyIcon,
  ColorWheelIcon,
  LayersIcon,
  DiscIcon,
  DashboardIcon,
  ImageIcon,
} from '@radix-ui/react-icons'

import type { BlockType } from '@basehub/data/replicache/index'
import { UnionBlockIcon } from '~/components/icons/union'
import { OgImageIcon } from '~/components/icons/og-image'
import { ListBlockViewIcon } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/list/views/view-switcher'
import { ListView } from '@basehub/data/replicache/index'
import { WorkflowBlockIcon } from '~/components/icons/workflow'
import { SVGIcon } from '~/components/icons/svg'

export type BlockMetadata = {
  label: string
  description: string
  icon: React.ReactNode
  hide?: boolean
  isNew?: boolean
} & (
  | {
      type: 'list'
      layout: ListView['layout']
    }
  | {
      type: 'media'
      preset: 'image' | 'video' | 'audio' | 'file' | 'all'
    }
  | {
      type: 'workflow'
      preset: 'ai' | 'all'
    }
  | {
      type: Exclude<BlockType, 'list' | 'media' | 'root' | 'workflow'>
    }
)

export const blockMetadata: BlockMetadata[] = [
  {
    type: 'text',
    label: 'Plain Text',
    description: 'For titles, names, paragraphs',
    icon: <TextIcon />,
  },
  {
    type: 'number',
    label: 'Number',
    description: 'For IDs, ratings, quantity',
    icon: <FrameIcon />,
  },
  {
    type: 'select',
    label: 'Select',
    description: 'Pick between a preset range of values',
    icon: <TextAlignJustifyIcon />,
  },
  {
    type: 'comment',
    label: 'Comment',
    description: 'For help text, or just a comment',
    icon: <PersonIcon />,
    hide: true,
  },
  {
    type: 'rich-text',
    label: 'Rich Text',
    description: 'For longer posts, or articles',
    icon: <InputIcon />,
  },
  {
    type: 'media',
    label: 'Media',
    description: 'Upload a new image, video, or any file',
    icon: <DashboardIcon />,
    preset: 'all',
    isNew: true,
  },
  {
    type: 'media',
    label: 'Image',
    description: 'Upload a new image',
    icon: <ImageIcon />,
    preset: 'image',
  },
  {
    preset: 'video',
    type: 'media',
    label: 'Video',
    description: 'Upload a new video',
    icon: <VideoIcon />,
  },
  {
    preset: 'audio',
    type: 'media',
    label: 'Audio',
    description: 'Upload an audio file',
    icon: <SpeakerLoudIcon />,
  },
  {
    preset: 'file',
    type: 'media',
    label: 'File',
    description: 'Upload any file',
    icon: <FileIcon />,
  },
  {
    type: 'boolean',
    label: 'Boolean',
    description: 'True or false, yes or no',
    icon: <ComponentBooleanIcon />,
  },
  {
    type: 'date',
    label: 'Date',
    description: 'Mark a point in time',
    icon: <CalendarIcon />,
  },
  {
    type: 'color',
    label: 'Color',
    description: 'Pick a color',
    icon: <ColorWheelIcon />,
  },
  {
    type: 'list',
    label: 'Table',
    layout: 'table',
    description: 'Display structured data',
    icon: <ListBlockViewIcon view={{ layout: 'table' }} />,
  },
  {
    type: 'list',
    label: 'Gallery',
    layout: 'gallery',
    description: 'Showcase visual content',
    icon: <ListBlockViewIcon view={{ layout: 'gallery' }} />,
  },
  {
    type: 'list',
    label: 'Inline list',
    layout: 'inline',
    description: 'Quick, concise list of items',
    icon: <ListBlockViewIcon view={{ layout: 'inline' }} />,
  },
  {
    type: 'code-snippet',
    label: 'Code Snippet',
    description: 'Capture a code snippet',
    icon: <CodeIcon />,
  },
  {
    type: 'document',
    label: 'Document',
    description: 'A nested page, a section',
    icon: <FileIcon />,
  },
  {
    type: 'instance',
    label: 'Instance',
    description: 'Create an instance of a component',
    icon: <ComponentInstanceIcon />,
  },
  {
    type: 'component',
    label: 'Component',
    description: 'A button, a section, or anything to reuse',
    icon: <Component1Icon />,
  },
  {
    type: 'reference',
    label: 'Reference',
    description: 'Reference an existing block or create a new one',
    icon: <UnionBlockIcon />,
  },
  {
    type: 'og-image',
    label: 'OG Image',
    description: 'Dynamic image for social media sharing',
    icon: <OgImageIcon />,
  },
  {
    type: 'variant-set',
    label: 'Variant Set',
    description: 'Add multi-language, A/B tests and more',
    icon: <LayersIcon />,
  },
  {
    type: 'icon',
    label: 'Icon',
    description: 'For rendering SVGs in your sites',
    icon: <SVGIcon />,
  },
  {
    type: 'event',
    label: 'Event',
    description: 'Track custom events and form submissions',
    icon: <DiscIcon />,
  },
  {
    type: 'workflow',
    label: 'Workflow',
    description: 'Create automations with actions and triggers',
    icon: <WorkflowBlockIcon />,
    preset: 'all',
  },
  {
    type: 'workflow',
    label: 'Workflow Autofill',
    description: 'Create automations with the help of AI',
    preset: 'ai',
    icon: <WorkflowBlockIcon />,
    isNew: true,
  },
] satisfies BlockMetadata[]
